/**
 * Thread-scoped right-panel surface state.
 *
 * This is intentionally a shallow workspace model: it owns an ordered set of
 * surface descriptors and the active surface, while each feature continues to
 * own its durable resource state. Browser surfaces point at preview tab ids,
 * file surfaces point at workspace paths, and diff/files remain singleton
 * surfaces.
 */
import { scopedThreadKey } from "@amoreg/client-runtime/environment";
import type { EnvironmentId, ScopedThreadRef } from "@amoreg/contracts";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

import { resolveStorage } from "./lib/storage";

export const RIGHT_PANEL_KINDS = [
  "diff",
  "files",
  "file",
  "attachment",
  "preview",
  "agents",
] as const;
export type RightPanelKind = (typeof RIGHT_PANEL_KINDS)[number];

export type RightPanelSurface =
  | { id: `browser:${string}`; kind: "preview"; resourceId: string }
  | { id: "browser:new"; kind: "preview"; resourceId: null }
  | { id: "diff"; kind: "diff" }
  | { id: "files"; kind: "files" }
  | {
      id: `file:${string}`;
      kind: "file";
      relativePath: string;
      revealLine: number | null;
      revealRequestId: number;
      /**
       * Set when the file belongs to another conversation of the same
       * organization: it is previewed here, read-only, from that
       * conversation's workspace. Absent for the thread's own files.
       */
      origin?: RightPanelFileOrigin;
    }
  | {
      /**
       * A message attachment (a document the person uploaded), previewed in
       * the panel the way a workspace file is, instead of in a modal. It is
       * not a workspace path: the bytes come from the attachment store (a
       * sent message) or from the composer draft (not sent yet).
       */
      id: `attachment:${string}`;
      kind: "attachment";
      attachment: RightPanelAttachment;
      source: RightPanelAttachmentSource;
    }
  | { id: "agents"; kind: "agents" };

export interface RightPanelAttachment {
  readonly id: string;
  readonly name: string;
  readonly mimeType: string;
  readonly sizeBytes: number;
}

/**
 * Where an attachment surface reads its bytes. A draft's `File` lives in the
 * composer draft store and is never persisted, so a `draft` surface does not
 * survive a reload either (see `persistableThreadState`).
 */
export type RightPanelAttachmentSource =
  | { readonly kind: "asset"; readonly environmentId: EnvironmentId }
  | { readonly kind: "draft" };

export interface RightPanelFileOrigin {
  readonly threadId: string;
  /** That conversation's workspace directory — the `cwd` the file RPCs take. */
  readonly cwd: string;
  readonly title: string;
}

export const RIGHT_PANEL_STORAGE_KEY = "amoreg:right-panel-state:v2";
// v9 removed the "plan" surface kind (plans render inline in the transcript).
// v10 removed the "terminal" surface kind (host terminals are operator-only).
const RIGHT_PANEL_STORAGE_VERSION = 10;

/**
 * How browser surfaces are kept. The desktop preview has one surface per
 * browser tab; the cloud browser is one surface (`single`) whose own tab
 * strip shows the browser's tabs, so a chat never holds a second one.
 */
export interface BrowserSurfaceOptions {
  readonly single?: boolean;
}

export interface ThreadRightPanelState {
  isOpen: boolean;
  activeSurfaceId: string | null;
  surfaces: RightPanelSurface[];
}

interface RightPanelStoreState {
  byThreadKey: Record<string, ThreadRightPanelState>;
  open: (ref: ScopedThreadRef, kind: Exclude<RightPanelKind, "file" | "attachment">) => void;
  openBrowser: (ref: ScopedThreadRef, tabId: string | null, options?: BrowserSurfaceOptions) => void;
  openFile: (ref: ScopedThreadRef, relativePath: string, line?: number) => void;
  /** Preview a file from another conversation in this thread's panel, read-only. */
  openForeignFile: (
    ref: ScopedThreadRef,
    origin: RightPanelFileOrigin,
    relativePath: string,
  ) => void;
  /** Preview a message attachment in the panel; one surface per attachment id. */
  openAttachment: (
    ref: ScopedThreadRef,
    attachment: RightPanelAttachment,
    source: RightPanelAttachmentSource,
  ) => void;
  activateSurface: (ref: ScopedThreadRef, surfaceId: string) => void;
  closeSurface: (ref: ScopedThreadRef, surfaceId: string) => void;
  closeOtherSurfaces: (ref: ScopedThreadRef, surfaceId: string) => void;
  closeSurfacesToRight: (ref: ScopedThreadRef, surfaceId: string) => void;
  closeAllSurfaces: (ref: ScopedThreadRef) => void;
  reconcileBrowserSurfaces: (
    ref: ScopedThreadRef,
    tabIds: readonly string[],
    options?: BrowserSurfaceOptions,
  ) => void;
  reconcileFileSurfaces: (ref: ScopedThreadRef, workspaceAvailable: boolean) => void;
  show: (ref: ScopedThreadRef) => void;
  close: (ref: ScopedThreadRef) => void;
  toggleVisibility: (ref: ScopedThreadRef) => void;
  toggle: (ref: ScopedThreadRef, kind: Exclude<RightPanelKind, "file" | "attachment">) => void;
  removeThread: (ref: ScopedThreadRef) => void;
}

const EMPTY_THREAD_STATE: ThreadRightPanelState = {
  isOpen: false,
  activeSurfaceId: null,
  surfaces: [],
};

const singletonSurface = (
  kind: Exclude<RightPanelKind, "file" | "attachment" | "preview">,
): RightPanelSurface => {
  switch (kind) {
    case "diff":
      return { id: "diff", kind };
    case "files":
      return { id: "files", kind };
    case "agents":
      return { id: "agents", kind };
  }
};

const browserSurface = (tabId: string | null): BrowserPanelSurface =>
  tabId
    ? { id: `browser:${tabId}`, kind: "preview", resourceId: tabId }
    : { id: "browser:new", kind: "preview", resourceId: null };

/**
 * A workspace-relative path never starts with `/`, so a leading slash
 * unambiguously marks a foreign file's id: `file:/<threadId>/<path>`.
 */
export const fileSurfaceId = (
  relativePath: string,
  origin?: RightPanelFileOrigin,
): `file:${string}` =>
  origin ? `file:/${origin.threadId}/${relativePath}` : `file:${relativePath}`;

const fileSurface = (
  relativePath: string,
  revealLine: number | null,
  revealRequestId: number,
  origin?: RightPanelFileOrigin,
): RightPanelSurface => ({
  id: fileSurfaceId(relativePath, origin),
  kind: "file",
  relativePath,
  revealLine,
  revealRequestId,
  ...(origin ? { origin } : {}),
});

export const attachmentSurfaceId = (attachmentId: string): `attachment:${string}` =>
  `attachment:${attachmentId}`;

const attachmentSurface = (
  attachment: RightPanelAttachment,
  source: RightPanelAttachmentSource,
): RightPanelSurface => ({
  id: attachmentSurfaceId(attachment.id),
  kind: "attachment",
  attachment,
  source,
});

const isAttachmentSurface = (
  surface: RightPanelSurface,
): surface is Extract<RightPanelSurface, { kind: "attachment" }> => surface.kind === "attachment";

/**
 * What a thread's panel state looks like in storage: a draft attachment's
 * bytes do not outlive the page, so its surface is left out rather than
 * reopening as an empty preview after a reload.
 */
export const persistableThreadState = (current: ThreadRightPanelState): ThreadRightPanelState => {
  const surfaces = current.surfaces.filter(
    (surface) => !(isAttachmentSurface(surface) && surface.source.kind === "draft"),
  );
  if (surfaces.length === current.surfaces.length) return current;
  const activeSurfaceId = surfaces.some((surface) => surface.id === current.activeSurfaceId)
    ? current.activeSurfaceId
    : (surfaces[0]?.id ?? null);
  return { isOpen: current.isOpen && surfaces.length > 0, activeSurfaceId, surfaces };
};

const openFileSurface = (
  current: ThreadRightPanelState,
  relativePath: string,
  revealLine: number | null,
  origin?: RightPanelFileOrigin,
): ThreadRightPanelState => {
  const withoutStandaloneExplorer = current.surfaces.filter((surface) => surface.kind !== "files");
  const surfaceId = fileSurfaceId(relativePath, origin);
  const existing = withoutStandaloneExplorer.find(
    (surface): surface is Extract<RightPanelSurface, { kind: "file" }> =>
      surface.id === surfaceId && surface.kind === "file",
  );
  const surface = fileSurface(
    relativePath,
    revealLine,
    (existing?.revealRequestId ?? 0) + 1,
    origin,
  );
  return {
    isOpen: true,
    activeSurfaceId: surface.id,
    surfaces: existing
      ? withoutStandaloneExplorer.map((entry) => (entry.id === surface.id ? surface : entry))
      : [...withoutStandaloneExplorer, surface],
  };
};

const upsertSurface = (
  current: ThreadRightPanelState,
  surface: RightPanelSurface,
  activate = true,
): ThreadRightPanelState => ({
  isOpen: true,
  surfaces: current.surfaces.some((entry) => entry.id === surface.id)
    ? current.surfaces
    : [...current.surfaces, surface],
  activeSurfaceId: activate ? surface.id : current.activeSurfaceId,
});

type BrowserPanelSurface = Extract<RightPanelSurface, { kind: "preview" }>;

const isBrowserSurface = (surface: RightPanelSurface): surface is BrowserPanelSurface =>
  surface.kind === "preview";

/**
 * The cloud browser's one surface: `surface` takes the place of the first
 * browser surface (or goes last), and every other browser surface goes. A
 * placeholder never replaces a surface that already names a tab. The active
 * surface stays where it was unless it was a browser surface, or `activate`.
 */
const withOneBrowserSurface = (
  current: ThreadRightPanelState,
  surface: BrowserPanelSurface,
  activate: boolean,
): ThreadRightPanelState => {
  const existing = current.surfaces.find(isBrowserSurface);
  const kept = surface.resourceId === null && existing ? existing : surface;
  let placed = false;
  const surfaces = current.surfaces.flatMap<RightPanelSurface>((entry) => {
    if (!isBrowserSurface(entry)) return [entry];
    if (placed) return [];
    placed = true;
    return [kept];
  });
  if (!placed) surfaces.push(kept);
  const activeWasBrowser = current.surfaces.some(
    (entry) => isBrowserSurface(entry) && entry.id === current.activeSurfaceId,
  );
  const activeSurfaceId =
    activate || activeWasBrowser
      ? kept.id
      : surfaces.some((entry) => entry.id === current.activeSurfaceId)
        ? current.activeSurfaceId
        : (surfaces[0]?.id ?? null);
  const unchanged =
    surfaces.length === current.surfaces.length &&
    surfaces.every((entry, index) => entry === current.surfaces[index]) &&
    activeSurfaceId === current.activeSurfaceId &&
    (!activate || current.isOpen);
  if (unchanged) return current;
  return { isOpen: activate ? true : current.isOpen, activeSurfaceId, surfaces };
};

/**
 * The cloud browser's surface after a new look at the chat's preview tabs:
 * the one it had while its tab is still there, else one for the first tab,
 * else (no tab known yet) a placeholder, so an open browser stays open.
 */
const reconcileOneBrowserSurface = (
  current: ThreadRightPanelState,
  tabIds: readonly string[],
): ThreadRightPanelState => {
  const existing = current.surfaces.find(isBrowserSurface);
  const firstTab = tabIds[0];
  if (existing?.resourceId && tabIds.includes(existing.resourceId)) {
    return withOneBrowserSurface(current, existing, false);
  }
  if (firstTab !== undefined) {
    return withOneBrowserSurface(current, browserSurface(firstTab), false);
  }
  if (!existing) return current;
  return withOneBrowserSurface(current, browserSurface(null), false);
};

const updateThread = (
  byThreadKey: Record<string, ThreadRightPanelState>,
  threadKey: string,
  updater: (current: ThreadRightPanelState) => ThreadRightPanelState,
): Record<string, ThreadRightPanelState> => {
  const current = byThreadKey[threadKey] ?? EMPTY_THREAD_STATE;
  const next = updater(current);
  if (!next.isOpen && next.activeSurfaceId === null && next.surfaces.length === 0) {
    if (!(threadKey in byThreadKey)) return byThreadKey;
    const { [threadKey]: _removed, ...rest } = byThreadKey;
    return rest;
  }
  if (next === current) return byThreadKey;
  return { ...byThreadKey, [threadKey]: next };
};

const isFileOrigin = (value: unknown): value is RightPanelFileOrigin =>
  typeof value === "object" &&
  value !== null &&
  typeof (value as RightPanelFileOrigin).threadId === "string" &&
  typeof (value as RightPanelFileOrigin).cwd === "string" &&
  typeof (value as RightPanelFileOrigin).title === "string";

const isPersistedAttachment = (value: unknown): value is RightPanelAttachment =>
  typeof value === "object" &&
  value !== null &&
  typeof (value as RightPanelAttachment).id === "string" &&
  typeof (value as RightPanelAttachment).name === "string" &&
  typeof (value as RightPanelAttachment).mimeType === "string" &&
  typeof (value as RightPanelAttachment).sizeBytes === "number";

function normalizeRevealLine(line: number | undefined): number | null {
  if (line === undefined || !Number.isFinite(line)) return null;
  return Math.max(1, Math.trunc(line));
}

export function migratePersistedRightPanelState(persistedState: unknown): {
  byThreadKey: Record<string, ThreadRightPanelState>;
} {
  if (!persistedState || typeof persistedState !== "object") {
    return { byThreadKey: {} };
  }
  const byThreadKey =
    "byThreadKey" in persistedState &&
    persistedState.byThreadKey &&
    typeof persistedState.byThreadKey === "object"
      ? Object.fromEntries(
          Object.entries(persistedState.byThreadKey as Record<string, ThreadRightPanelState>).map(
            ([threadKey, threadState]) => {
              const validThreadState =
                threadState && typeof threadState === "object" ? threadState : null;
              const surfaces = Array.isArray(validThreadState?.surfaces)
                ? validThreadState.surfaces.flatMap<RightPanelSurface>((surface) => {
                    // Dropped surface kinds: plans now render inline in the
                    // transcript (v9); host terminals are operator-only and
                    // left the panel (v10).
                    const kind = (surface as { kind?: string }).kind;
                    if (kind === "plan" || kind === "terminal") return [];
                    if (surface.kind === "file") {
                      // A foreign file whose origin did not survive would
                      // open as one of this thread's own paths: drop it.
                      const origin = (surface as { origin?: unknown }).origin;
                      if (origin !== undefined && !isFileOrigin(origin)) return [];
                      const revealLine =
                        typeof surface.revealLine === "number" &&
                        Number.isFinite(surface.revealLine)
                          ? Math.max(1, Math.trunc(surface.revealLine))
                          : null;
                      const revealRequestId =
                        typeof surface.revealRequestId === "number" &&
                        Number.isSafeInteger(surface.revealRequestId) &&
                        surface.revealRequestId >= 0
                          ? surface.revealRequestId
                          : 0;
                      return [{ ...surface, revealLine, revealRequestId }];
                    }
                    if (surface.kind === "attachment") {
                      // Only a stored attachment can be reopened; anything
                      // malformed or draft-sourced is dropped.
                      const candidate = surface as {
                        attachment?: unknown;
                        source?: { kind?: unknown; environmentId?: unknown };
                      };
                      return isPersistedAttachment(candidate.attachment) &&
                        candidate.source?.kind === "asset" &&
                        typeof candidate.source.environmentId === "string" &&
                        surface.id === attachmentSurfaceId(candidate.attachment.id)
                        ? [surface]
                        : [];
                    }
                    return [surface];
                  })
                : [];
              const persistedActiveSurfaceId = surfaces.some(
                (surface) => surface.id === validThreadState?.activeSurfaceId,
              )
                ? (validThreadState?.activeSurfaceId ?? null)
                : null;
              // A migration that dropped every surface (e.g. plan-only panels
              // in v9) must not reopen an empty panel.
              const isOpen =
                surfaces.length > 0 &&
                (typeof validThreadState?.isOpen === "boolean"
                  ? validThreadState.isOpen
                  : persistedActiveSurfaceId !== null);
              // An open panel needs an active surface: if migration dropped
              // the persisted one (e.g. plan was active), fall back to the
              // first survivor instead of rendering an open empty panel.
              const activeSurfaceId =
                persistedActiveSurfaceId ?? (isOpen ? (surfaces[0]?.id ?? null) : null);
              return [threadKey, { isOpen, surfaces, activeSurfaceId }];
            },
          ),
        )
      : {};
  return { byThreadKey };
}

export const useRightPanelStore = create<RightPanelStoreState>()(
  persist(
    (set) => ({
      byThreadKey: {},
      open: (ref, kind) =>
        set((state) => ({
          byThreadKey: updateThread(state.byThreadKey, scopedThreadKey(ref), (current) => {
            if (kind === "preview") {
              const existing = current.surfaces.find((surface) => surface.kind === "preview");
              return upsertSurface(current, existing ?? browserSurface(null));
            }
            return upsertSurface(current, singletonSurface(kind));
          }),
        })),
      openBrowser: (ref, tabId, options) =>
        set((state) => ({
          byThreadKey: updateThread(state.byThreadKey, scopedThreadKey(ref), (current) => {
            const surface = browserSurface(tabId);
            if (options?.single) {
              return withOneBrowserSurface(current, surface, true);
            }
            const withoutPlaceholder = tabId
              ? current.surfaces.filter((entry) => entry.id !== "browser:new")
              : current.surfaces;
            return upsertSurface({ ...current, surfaces: withoutPlaceholder }, surface);
          }),
        })),
      openFile: (ref, relativePath, line) =>
        set((state) => ({
          byThreadKey: updateThread(state.byThreadKey, scopedThreadKey(ref), (current) =>
            openFileSurface(current, relativePath, normalizeRevealLine(line)),
          ),
        })),
      openForeignFile: (ref, origin, relativePath) =>
        set((state) => ({
          byThreadKey: updateThread(state.byThreadKey, scopedThreadKey(ref), (current) =>
            openFileSurface(current, relativePath, null, origin),
          ),
        })),
      openAttachment: (ref, attachment, source) =>
        set((state) => ({
          byThreadKey: updateThread(state.byThreadKey, scopedThreadKey(ref), (current) => {
            const surface = attachmentSurface(attachment, source);
            // The same attachment reopened from a different place (draft chip,
            // then the sent message) keeps one tab, pointing at the latest source.
            const existing = current.surfaces.some((entry) => entry.id === surface.id);
            return {
              isOpen: true,
              activeSurfaceId: surface.id,
              surfaces: existing
                ? current.surfaces.map((entry) => (entry.id === surface.id ? surface : entry))
                : [...current.surfaces, surface],
            };
          }),
        })),
      activateSurface: (ref, surfaceId) =>
        set((state) => ({
          byThreadKey: updateThread(state.byThreadKey, scopedThreadKey(ref), (current) =>
            current.surfaces.some((surface) => surface.id === surfaceId)
              ? { ...current, isOpen: true, activeSurfaceId: surfaceId }
              : current,
          ),
        })),
      closeSurface: (ref, surfaceId) =>
        set((state) => ({
          byThreadKey: updateThread(state.byThreadKey, scopedThreadKey(ref), (current) => {
            const index = current.surfaces.findIndex((surface) => surface.id === surfaceId);
            if (index < 0) return current;
            const surfaces = current.surfaces.filter((surface) => surface.id !== surfaceId);
            if (current.activeSurfaceId !== surfaceId) {
              return { ...current, isOpen: surfaces.length > 0 && current.isOpen, surfaces };
            }
            const fallback = surfaces[Math.min(index, surfaces.length - 1)] ?? null;
            return {
              ...current,
              isOpen: surfaces.length > 0 && current.isOpen,
              surfaces,
              activeSurfaceId: fallback?.id ?? null,
            };
          }),
        })),
      closeOtherSurfaces: (ref, surfaceId) =>
        set((state) => ({
          byThreadKey: updateThread(state.byThreadKey, scopedThreadKey(ref), (current) => {
            const surface = current.surfaces.find((entry) => entry.id === surfaceId);
            if (!surface || current.surfaces.length === 1) return current;
            return {
              ...current,
              isOpen: true,
              surfaces: [surface],
              activeSurfaceId: surface.id,
            };
          }),
        })),
      closeSurfacesToRight: (ref, surfaceId) =>
        set((state) => ({
          byThreadKey: updateThread(state.byThreadKey, scopedThreadKey(ref), (current) => {
            const index = current.surfaces.findIndex((surface) => surface.id === surfaceId);
            if (index < 0 || index === current.surfaces.length - 1) return current;
            const surfaces = current.surfaces.slice(0, index + 1);
            const activeStillExists = surfaces.some(
              (surface) => surface.id === current.activeSurfaceId,
            );
            return {
              ...current,
              surfaces,
              activeSurfaceId: activeStillExists ? current.activeSurfaceId : surfaceId,
            };
          }),
        })),
      closeAllSurfaces: (ref) =>
        set((state) => ({
          byThreadKey: updateThread(state.byThreadKey, scopedThreadKey(ref), (current) =>
            current.surfaces.length === 0
              ? current
              : { ...current, isOpen: false, surfaces: [], activeSurfaceId: null },
          ),
        })),
      reconcileBrowserSurfaces: (ref, tabIds, options) =>
        set((state) => ({
          byThreadKey: updateThread(state.byThreadKey, scopedThreadKey(ref), (current) => {
            if (options?.single) return reconcileOneBrowserSurface(current, tabIds);
            const validIds = new Set(tabIds.map((tabId) => `browser:${tabId}`));
            const nonBrowser = current.surfaces.filter((surface) => surface.kind !== "preview");
            const existingBrowser = current.surfaces.filter(
              (surface): surface is Extract<RightPanelSurface, { kind: "preview" }> =>
                surface.kind === "preview" &&
                surface.id !== "browser:new" &&
                validIds.has(surface.id),
            );
            const knownIds = new Set(existingBrowser.map((surface) => surface.id));
            const added = tabIds
              .filter((tabId) => !knownIds.has(`browser:${tabId}`))
              .map((tabId) => browserSurface(tabId));
            const surfaces = [...nonBrowser, ...existingBrowser, ...added];
            const activeStillExists = surfaces.some(
              (surface) => surface.id === current.activeSurfaceId,
            );
            const fallbackBrowser = surfaces.find((surface) => surface.kind === "preview");
            return {
              ...current,
              surfaces,
              activeSurfaceId: activeStillExists
                ? current.activeSurfaceId
                : (fallbackBrowser?.id ?? surfaces[0]?.id ?? null),
            };
          }),
        })),
      reconcileFileSurfaces: (ref, workspaceAvailable) =>
        set((state) => ({
          byThreadKey: updateThread(state.byThreadKey, scopedThreadKey(ref), (current) => {
            if (workspaceAvailable) return current;
            const surfaces = current.surfaces.filter(
              (surface) => surface.kind !== "files" && surface.kind !== "file",
            );
            if (surfaces.length === current.surfaces.length) return current;
            const activeStillExists = surfaces.some(
              (surface) => surface.id === current.activeSurfaceId,
            );
            return {
              ...current,
              isOpen: surfaces.length > 0 ? current.isOpen : false,
              surfaces,
              activeSurfaceId: activeStillExists
                ? current.activeSurfaceId
                : (surfaces.at(-1)?.id ?? null),
            };
          }),
        })),
      show: (ref) =>
        set((state) => ({
          byThreadKey: updateThread(state.byThreadKey, scopedThreadKey(ref), (current) =>
            current.isOpen ? current : { ...current, isOpen: true },
          ),
        })),
      close: (ref) =>
        set((state) => ({
          byThreadKey: updateThread(state.byThreadKey, scopedThreadKey(ref), (current) =>
            current.isOpen ? { ...current, isOpen: false } : current,
          ),
        })),
      toggleVisibility: (ref) =>
        set((state) => ({
          byThreadKey: updateThread(state.byThreadKey, scopedThreadKey(ref), (current) => ({
            ...current,
            isOpen: !current.isOpen,
          })),
        })),
      toggle: (ref, kind) =>
        set((state) => ({
          byThreadKey: updateThread(state.byThreadKey, scopedThreadKey(ref), (current) => {
            const active = current.surfaces.find(
              (surface) => surface.id === current.activeSurfaceId,
            );
            if (current.isOpen && active?.kind === kind) {
              return { ...current, isOpen: false };
            }
            if (kind === "preview") {
              const existing = current.surfaces.find((surface) => surface.kind === "preview");
              return upsertSurface(current, existing ?? browserSurface(null));
            }
            return upsertSurface(current, singletonSurface(kind));
          }),
        })),
      removeThread: (ref) =>
        set((state) => {
          const threadKey = scopedThreadKey(ref);
          if (!(threadKey in state.byThreadKey)) return state;
          const { [threadKey]: _removed, ...rest } = state.byThreadKey;
          return { byThreadKey: rest };
        }),
    }),
    {
      name: RIGHT_PANEL_STORAGE_KEY,
      version: RIGHT_PANEL_STORAGE_VERSION,
      storage: createJSONStorage(() =>
        resolveStorage(typeof window !== "undefined" ? window.localStorage : undefined),
      ),
      partialize: (state) => ({
        byThreadKey: Object.fromEntries(
          Object.entries(state.byThreadKey).map(([threadKey, threadState]) => [
            threadKey,
            persistableThreadState(threadState),
          ]),
        ),
      }),
      migrate: migratePersistedRightPanelState,
    },
  ),
);

export function selectThreadRightPanelState(
  byThreadKey: Record<string, ThreadRightPanelState>,
  ref: ScopedThreadRef | null | undefined,
): ThreadRightPanelState {
  if (!ref) return EMPTY_THREAD_STATE;
  return byThreadKey[scopedThreadKey(ref)] ?? EMPTY_THREAD_STATE;
}

export function selectActiveRightPanel(
  byThreadKey: Record<string, ThreadRightPanelState>,
  ref: ScopedThreadRef | null | undefined,
): RightPanelKind | null {
  const state = selectThreadRightPanelState(byThreadKey, ref);
  if (!state.isOpen) return null;
  return state.surfaces.find((surface) => surface.id === state.activeSurfaceId)?.kind ?? null;
}

export function selectActiveRightPanelSurface(
  byThreadKey: Record<string, ThreadRightPanelState>,
  ref: ScopedThreadRef | null | undefined,
): RightPanelSurface | null {
  const state = selectThreadRightPanelState(byThreadKey, ref);
  if (!state.isOpen) return null;
  return state.surfaces.find((surface) => surface.id === state.activeSurfaceId) ?? null;
}
